import { z } from 'zod';

/**
 * Schema helper utilities for lenient deserialization
 * These helpers are used internally for migrating old data
 * and should not be exported from the package
 */

/**
 * Creates a lenient string schema that:
 * - Returns default value if input is not a string
 * - Truncates strings that exceed maxLength
 * - Never throws during deserialization
 * 
 * @param maxLength Maximum allowed string length
 * @param defaultValue Default value if input is invalid
 * @returns Zod schema for lenient string validation
 */
export const lenientString = (maxLength: number, defaultValue: string = '') =>
  z.string()
    .catch(defaultValue)
    .transform(val => val.slice(0, maxLength))
    .pipe(z.string().max(maxLength));

/**
 * Creates a lenient UUID string schema that:
 * - Returns a new UUID if input is invalid
 * - Validates UUID format
 * 
 * @returns Zod schema for lenient UUID validation
 */
export const lenientUuid = () =>
  z.string()
    .uuid()
    .catch(() => crypto.randomUUID());

/**
 * Creates a lenient date string schema (ISO format YYYY-MM-DD) that:
 * - Returns default value if input is not a valid date string
 * - Truncates to 10 characters
 * 
 * @param defaultValue Default value if input is invalid (empty string by default)
 * @returns Zod schema for lenient date string validation
 */
export const lenientDateString = (defaultValue: string = '') =>
  z.string()
    .catch(defaultValue)
    .transform(val => val.slice(0, 10))
    .pipe(z.string().max(10));

/**
 * Creates a lenient number schema that:
 * - Returns default value if input is not a number
 * - Optionally applies min/max constraints
 * 
 * @param defaultValue Default value if input is invalid
 * @param min Optional minimum value
 * @param max Optional maximum value
 * @returns Zod schema for lenient number validation
 */
export const lenientNumber = (defaultValue: number, min?: number, max?: number) => {
  let schema = z.number();
  
  if (min !== undefined) {
    schema = schema.min(min);
  }
  
  if (max !== undefined) {
    schema = schema.max(max);
  }
  
  return schema.catch(defaultValue);
};

/**
 * Creates a lenient currency schema that:
 * - Returns default value if input is not a number
 * - Rounds to 2 decimal places (nearest penny)
 * - Optionally applies min/max constraints
 * 
 * @param defaultValue Default value if input is invalid
 * @param min Optional minimum value
 * @param max Optional maximum value
 * @returns Zod schema for lenient currency validation
 */
export const lenientCurrency = (defaultValue: number, min?: number, max?: number) => {
  return z.number()
    .catch(defaultValue)
    .transform(val => {
      const rounded = Math.round(val * 100) / 100;
      if (min !== undefined && rounded < min) return min;
      if (max !== undefined && rounded > max) return max;
      return rounded;
    });
};

/**
 * Creates a lenient enum schema that:
 * - Returns default value if input is not a valid enum member
 * 
 * @param enumObj The enum object
 * @param defaultValue Default enum value if input is invalid
 * @returns Zod schema for lenient enum validation
 */
export const lenientEnum = <T extends Record<string, any>>(
  enumObj: T,
  defaultValue: T[keyof T]
) =>
  z.nativeEnum(enumObj).catch(defaultValue);

/**
 * Creates a lenient array schema for deserializing node arrays
 * Handles undefined/missing values gracefully by returning empty array
 * 
 * @param deserializeFn Function to deserialize each array element
 * @returns Zod schema that always returns an array
 * 
 * @example
 * // Instead of: z.unknown().optional().transform(val => Array.isArray(val) ? val.map(v => NodeType.deserialize(v)) : [])
 * // Use: lenientNodeArray(NodeType.deserialize)
 */
export const lenientNodeArray = <T>(deserializeFn: (val: unknown) => T) =>
  z.unknown()
    .optional()
    .transform(val => 
      Array.isArray(val) ? val.map(deserializeFn) : []
    );

/**
 * Creates a lenient nested object schema for required fields
 * Uses createDefault() when value is missing
 * 
 * @param nodeNamespace Node namespace with deserialize and createDefault methods
 * @returns Zod schema that always returns a valid node
 * 
 * @example
 * // Instead of: z.unknown().optional().transform(val => val ? ValueHistoryNode.deserialize(val) : ValueHistoryNode.createDefault())
 * // Use: lenientRequiredNode(ValueHistoryNode)
 */
export const lenientRequiredNode = <T extends { deserialize: (val: unknown) => any, createDefault: () => any }>(
  nodeNamespace: T
) =>
  z.unknown()
    .optional()
    .transform(val => 
      val ? nodeNamespace.deserialize(val) : nodeNamespace.createDefault()
    );

/**
 * Creates a lenient nested object schema for optional fields
 * Returns undefined when value is missing
 * 
 * @param nodeNamespace Node namespace with deserialize method
 * @returns Zod schema that returns node or undefined
 * 
 * @example
 * // Instead of: z.unknown().optional().transform(val => val ? ContactNode.deserialize(val) : undefined)
 * // Use: lenientOptionalNode(ContactNode)
 */
export const lenientOptionalNode = <T extends { deserialize: (val: unknown) => any }>(
  nodeNamespace: T
) =>
  z.unknown()
    .optional()
    .transform(val => 
      val ? nodeNamespace.deserialize(val) : undefined
    );

/**
 * Creates a lenient boolean schema that:
 * - Returns default value if input is not a boolean
 * - Coerces string "true"/"false" to boolean
 * 
 * @param defaultValue Default value if input is invalid
 * @returns Zod schema for lenient boolean validation
 */
export const lenientBoolean = (defaultValue: boolean = false) =>
  z.union([z.boolean(), z.string()])
    .catch(defaultValue)
    .transform(val => {
      if (typeof val === 'boolean') return val;
      if (val === 'true') return true;
      if (val === 'false') return false;
      return defaultValue;
    });

/**
 * Creates a lenient optional string schema that:
 * - Returns empty string if input is not a string
 * - Truncates strings that exceed maxLength
 * - Can be chained with .optional() for truly optional fields
 * 
 * @param maxLength Maximum allowed string length
 * @returns Zod schema for optional string fields
 */
export const lenientOptionalString = (maxLength: number) =>
  z.string()
    .catch('')
    .transform(val => val.slice(0, maxLength))
    .pipe(z.string().max(maxLength));
